import { apiClient } from '@/lib/api-client';
import type { InferenceRun } from '../types';

export interface SubmitInferenceArgs {
  imageBlob: Blob;
  modelKeys: string[];
  clientTrack: 'react-pwa' | 'vanilla-pwa' | 'expo-native';
  promptVersion?: string;
  countPromptVersion?: string;
  deviceUserAgent?: string;
  /**
   * When true and the decoded QR doesn't match any existing item, the API
   * will seed a new item (Name = `itemName` if provided, else the QR text)
   * and continue with the inference instead of returning item_not_found.
   */
  autoSeedItem?: boolean;
  /** Optional name for the seeded item; ignored when `autoSeedItem` is false. */
  itemName?: string;
}

export type SubmitInferenceResult =
  | { kind: 'ok'; run: InferenceRun }
  | { kind: 'qr_not_detected'; message: string }
  | { kind: 'item_not_found'; message: string }
  | { kind: 'validation_error'; message: string }
  | { kind: 'error'; message: string };

export async function submitInference(args: SubmitInferenceArgs): Promise<SubmitInferenceResult> {
  const form = new FormData();
  form.append('image', args.imageBlob, 'capture.jpg');
  form.append('models', args.modelKeys.join(','));
  form.append('client_track', args.clientTrack);
  if (args.promptVersion) form.append('prompt_version', args.promptVersion);
  if (args.countPromptVersion) form.append('count_prompt_version', args.countPromptVersion);
  if (args.deviceUserAgent) form.append('device_user_agent', args.deviceUserAgent);
  if (args.autoSeedItem) form.append('auto_seed_item', 'true');
  if (args.itemName) form.append('item_name', args.itemName);

  try {
    const res = await apiClient.post<{ data: InferenceRun }>('/api/v1/inferences', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { kind: 'ok', run: res.data.data };
  } catch (err: any) {
    const status = err?.response?.status;
    const message = err?.response?.data?.message ?? err?.message ?? 'submission failed';
    if (status === 422) return { kind: 'qr_not_detected', message };
    if (status === 404) return { kind: 'item_not_found', message };
    if (status === 400) return { kind: 'validation_error', message };
    return { kind: 'error', message };
  }
}

export async function patchOperatorTruth(runId: string, operatorTruth: number): Promise<void> {
  await apiClient.patch(`/api/v1/inferences/${runId}/truth`, { operator_truth: operatorTruth });
}
